import { Form, Modal, notification } from 'antd';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { auth } from 'app/modules/Auth/firebaseConfig';
import { endSession, removeTokenFromLocal } from 'app/modules/Auth/services';
import { LOGIN } from 'configs/routeNames';
import { NotificationProps } from 'utils/types';
import { ProfileFormValues, ProfileUpdateResponse } from '../types';
import { getProfilePictureFileList, transformFile } from 'utils/file';
import dayjs from 'dayjs';
import { UseMutationResult } from '@tanstack/react-query';
import { ErrorMessage, getErrorMessage } from 'utils/axios';
import { UserInfoResponse } from 'app/modules/Auth/types';

export const useProfileHandlers = (
  form: ReturnType<typeof Form.useForm<ProfileFormValues>>[0],
  userInfo: UserInfoResponse,
  selectedCountry: { code: string; iso: string | undefined },
  ssoState: { ssoEnabled: boolean; removeSso: boolean },
  toggleSSO: () => void,
  removeSSO: () => void,
  setPreviewImage: (image: string | null) => void,
  setIsFormChanged: (changed: boolean) => void,
  setIsDiscardModalOpen: (open: boolean) => void,
  updateMutation: UseMutationResult<ProfileUpdateResponse, ErrorMessage, FormData>,
) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Add endSession mutation similar to Header component
  const endSessionApi = useMutation({
    mutationKey: ['endSession'],
    mutationFn: endSession,
    onSuccess: () => {
      removeTokenFromLocal();
    },
    onError: (error: ErrorMessage) => {
      notification.error({
        message: getErrorMessage(error),
        ...NotificationProps,
      });
    },
  });

  const handleSubmit = async (values: ProfileFormValues) => {
    const { profilePicture, ...restValues } = values;

    // Create profileDTO with required fields
    const profileDTO = {
      phoneCountryIso: selectedCountry.iso,
      phoneCountryCode: selectedCountry.code,
      contactNumber: restValues.contactNumber,
      department: restValues.department,
      employeeId: restValues.employeeId,
      ssoEnabled: ssoState.ssoEnabled,
      agentId: userInfo?.agentId,
      agencyId: userInfo?.agency?.id,
      companyId: userInfo?.agency?.companyId,
    };

    const formData = new FormData();
    formData.append('profileDTO', JSON.stringify(profileDTO));

    // Only append if it's a new file (has originFileObj)
    if (profilePicture && profilePicture.length > 0) {
      const fileObj = profilePicture[0];
      if (fileObj.originFileObj) {
        const transformedFile = transformFile(fileObj.originFileObj);
        formData.append('profilePicture', transformedFile, fileObj.name || 'profile.jpg');
      }
    }

    if (!userInfo?.agentId || !userInfo?.agency?.id || !userInfo?.agency?.companyId) {
      notification.error({
        message: t('errors.missingAgentOrAgency'),
        ...NotificationProps,
      });
      return;
    }

    updateMutation.mutate(formData);
  };

  const handleDiscard = () => {
    setIsDiscardModalOpen(false);
    setPreviewImage(null);
    setIsFormChanged(false); // Reset form changed state
    if (userInfo) {
      const cleanedUpSingleObject: ProfileFormValues = {};

      cleanedUpSingleObject.name = userInfo.fullName ?? '';
      cleanedUpSingleObject.surname = userInfo.surName ?? '';
      cleanedUpSingleObject.email = userInfo.email ?? '';
      cleanedUpSingleObject.cpf = userInfo.cpf ?? '';
      cleanedUpSingleObject.employeeId = userInfo.employeeId ?? '';
      cleanedUpSingleObject.contactNumber = userInfo.contactNumber ?? '';
      cleanedUpSingleObject.department = userInfo.department ?? '';
      cleanedUpSingleObject.roles = userInfo.roles?.map((role) => (typeof role === 'string' ? role : role.name)) ?? [];
      cleanedUpSingleObject.registrationNumber = userInfo.registrationNumber ?? '';
      cleanedUpSingleObject.dob = userInfo.dob ? dayjs(userInfo.dob) : undefined;
      cleanedUpSingleObject.profilePicture = userInfo.photo ? getProfilePictureFileList(userInfo.photo) : undefined;

      // Set the hidden form fields for contact number country code and ISO
      if (userInfo.phoneCountryCode && userInfo.phoneCountryIso) {
        cleanedUpSingleObject.contactNumberCountryCode = userInfo.phoneCountryCode;
        cleanedUpSingleObject.contactNumberCountryIso = userInfo.phoneCountryIso;
      }

      form.setFieldsValue(cleanedUpSingleObject);
    }
  };

  const handleServerLogout = async () => {
    // 1. Cancel all in-flight queries first to prevent API calls
    queryClient.cancelQueries();

    // 2. Remove specific queries without refetching
    queryClient.removeQueries({ queryKey: ['auth', 'user-info'] });
    queryClient.removeQueries({ queryKey: ['onboarding-messages'] });

    // 3. Clear entire query cache to ensure clean state (fixes userId cache issue without triggering APIs)
    queryClient.clear();

    // 4. Clear local storage
    localStorage.clear();

    // 5. Sign out from Firebase
    auth.signOut();

    // 6. Navigate immediately to prevent component remounts from triggering queries
    navigate(`/${LOGIN}`, { replace: true, state: {} });

    // 7. Call end session API (non-blocking, can happen after navigation)
    endSessionApi.mutate();
  };

  const handleSSOToggle = () => {
    const newSSOState = !ssoState.ssoEnabled;
    const modalTitle = newSSOState ? t('profile-settings.sso.confirmEnable') : t('profile-settings.sso.confirmDisable');
    const modalContent = newSSOState
      ? t('profile-settings.sso.confirmEnableMessage')
      : t('profile-settings.sso.confirmDisableMessage');

    Modal.confirm({
      title: modalTitle,
      content: modalContent,
      okText: t('profile-settings.sso.confirm'),
      cancelText: t('profile-settings.sso.cancel'),
      centered: true,
      onOk: () => {
        // Create the same payload as in handleSubmit but with toggled SSO state
        const profileDTO = {
          phoneCountryIso: selectedCountry.iso,
          phoneCountryCode: selectedCountry.code,
          contactNumber: form.getFieldValue('contactNumber'),
          department: form.getFieldValue('department'),
          employeeId: form.getFieldValue('employeeId'),
          ssoEnabled: newSSOState,
          agentId: userInfo?.agentId,
          agencyId: userInfo?.agency?.id,
          companyId: userInfo?.agency?.companyId,
        };

        const formData = new FormData();
        formData.append('profileDTO', JSON.stringify(profileDTO));

        if (!userInfo?.agentId || !userInfo?.agency?.id || !userInfo?.agency?.companyId) {
          notification.error({
            message: t('errors.missingAgentOrAgency'),
            ...NotificationProps,
          });
          return;
        }

        updateMutation.mutate(formData, {
          onSuccess: async (data: ProfileUpdateResponse) => {
            // Don't handle logout here - let the main updateMutation.onSuccess handle it
            if (data?.logout === true) {
              return;
            }

            // Update SSO state after successful API call
            if (newSSOState) {
              toggleSSO();
            } else {
              removeSSO();
            }
          },
        });
      },
    });
  };

  const handleSSODisable = () => {
    Modal.confirm({
      title: t('profile-settings.sso.confirmDisable'),
      content: t('profile-settings.sso.confirmDisableMessage'),
      okText: t('profile-settings.sso.confirm'),
      cancelText: t('profile-settings.sso.cancel'),
      centered: true,
      onOk: () => {
        // Create the same payload as in handleSubmit but with SSO disabled
        const profileDTO = {
          phoneCountryIso: selectedCountry.iso,
          phoneCountryCode: selectedCountry.code,
          contactNumber: form.getFieldValue('contactNumber'),
          department: form.getFieldValue('department'),
          employeeId: form.getFieldValue('employeeId'),
          ssoEnabled: false,
          agentId: userInfo?.agentId,
          agencyId: userInfo?.agency?.id,
          companyId: userInfo?.agency?.companyId,
        };

        const formData = new FormData();
        formData.append('profileDTO', JSON.stringify(profileDTO));

        if (!userInfo?.agentId || !userInfo?.agency?.id || !userInfo?.agency?.companyId) {
          notification.error({
            message: t('errors.missingAgentOrAgency'),
            ...NotificationProps,
          });
          return;
        }

        updateMutation.mutate(formData, {
          onSuccess: async (data: ProfileUpdateResponse) => {
            // Don't handle logout here - let the main updateMutation.onSuccess handle it
            if (data?.logout === true) {
              return;
            }

            // Remove SSO state after successful API call
            removeSSO();
          },
        });
      },
    });
  };

  return {
    handleSubmit,
    handleDiscard,
    handleServerLogout,
    handleSSOToggle,
    handleSSODisable,
  };
};
